<script setup lang="ts">
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { sanitizeAndCamelCaseTitle } from '@@/bits/sanitize_and_transform_text'
import { getTailwindBgColorClass, getTailwindColorClass } from '@@/bits/whiteboard'
import { SHAPE_BORDER_TO_FAKE_BORDER_COLOR_MAP, SHAPE_BORDER_TO_FAKE_FILL_COLOR_MAP } from '@@/bits/whiteboard_tools'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import WhiteboardSvgIcon, { SVG_ICONS } from '@@/vuecomponents/WhiteboardSvgIcon.vue'
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    colorScheme?: 'dark' | 'light'
    iconName?: string
    selectedBackgroundColor?: string | null
    iconColor?: string | null
    isSelected?: boolean
    label?: string
    htmlLabel?: string
    shortcut?: string
    xTooltip?: boolean
    mode: 'toolbar' | 'toolbarHorizontal' | 'elementModifierPrimaryMenu' | 'elementModifierSecondaryMenu'
  }>(),
  {
    colorScheme: 'dark',
    iconName: '',
    selectedBackgroundColor: null,
    iconColor: null,
    isSelected: false,
    label: '',
    htmlLabel: '',
    shortcut: '',
    xTooltip: false,
  },
)

const emit = defineEmits(['hoveron', 'hoveroff', 'click'])

const isHovered = ref(false)
const isActive = ref(false)
function onPointerEnter(): void {
  isHovered.value = true
  emit('hoveron')
}
function onPointerLeave(): void {
  isHovered.value = false
  emit('hoveroff')
}

function onPointerDown(event: PointerEvent): void {
  isActive.value = true
  emit('click', event)
  // To prevent the button from staying focused after the click
  // So that when user use a keyboard shortcut to trigger another button, this button will become :focus-visible
  unfocusButton()
}
function onPointerUp(): void {
  isActive.value = false
}
function unfocusButton(): void {
  if (buttonEl.value) {
    buttonEl.value.blur()
  }
}

const buttonRef = ref<typeof OzBaseButton | null>(null)
const buttonEl = computed(() => buttonRef.value?.$el)

const hasIcon = computed(() => props.iconName !== '')
const isSvgIcon = computed(() => SVG_ICONS.includes(props.iconName))
const isColorIcon = computed(() => props.iconName === 'color')
const isShapeIcon = computed(() => props.iconName.startsWith('geo'))
const isFillTypeIcon = computed(() =>
  [
    'fill-none',
    'fill-white',
    'fill-solid',
    'fill-none-element-modifier',
    'fill-white-element-modifier',
    'fill-solid-element-modifier',
  ].includes(props.iconName),
)
const isElementModifierIcon = computed(
  () => props.mode === 'elementModifierPrimaryMenu' || props.mode === 'elementModifierSecondaryMenu',
)
const isElementModifierSecondaryIcon = computed(() => props.mode === 'elementModifierSecondaryMenu')
const iconColor = computed((): string | null => {
  if (props.iconColor) {
    return props.iconColor
  }
  return null
})
const oriconIconNames = [
  'more_vertical_thin',
  'link',
  'delete',
  'pencil_outline',
  'fx_squircle',
  'fx_checked',
  'poll_open',
  'poll_close',
  'download_outline',
  'bold',
  'italic',
  'strikethrough',
  'text',
  'bulleted_list',
  'numbered_list',
]
const iconFont = computed(() => {
  if (oriconIconNames.includes(props.iconName)) {
    return 'oricon'
  }
  return 'wbicons'
})
const bgColorClass = computed(() => {
  if (isColorIcon.value && !isElementModifierIcon.value) {
    return 'bg-transparent'
  }
  if (!props.isSelected) {
    return null
  }
  if (props.selectedBackgroundColor) {
    return getTailwindBgColorClass(props.selectedBackgroundColor)
  }
  // For element modifier icons, show this grey background color
  if (isElementModifierIcon.value) {
    return 'bg-button-hover-light'
  }
  if (props.colorScheme === 'dark') {
    if (['fill-white', 'fill-none', 'fill-solid'].includes(props.iconName) && props.iconColor === 'light-text-100') {
      return 'bg-grey-300'
    }
    return 'bg-light-ui-100'
  }

  if (props.colorScheme === 'light') {
    if (['fill-white', 'fill-none', 'fill-solid'].includes(props.iconName) && props.iconColor === 'dark-text-100') {
      return 'bg-grey-400'
    }
    return 'bg-dark-ui-100'
  }
  return null
})
const iconColorClass = computed(() => {
  if (iconColor.value != null) {
    return getTailwindColorClass(SHAPE_BORDER_TO_FAKE_BORDER_COLOR_MAP[iconColor.value] ?? iconColor.value)
  }
  if (isElementModifierIcon.value && props.isSelected) {
    return isAppUsing('sandboxTextFormatting') ? 'text-canary-500' : 'text-light-text-200'
  }

  if (bgColorClass.value === 'bg-dark-text-100') {
    return 'text-light-text-100'
  }
  if (bgColorClass.value === 'bg-light-text-100') {
    return 'text-dark-text-100'
  }
  return null
})

const extraClass = computed<string>(() => {
  if (
    props.colorScheme === 'dark' &&
    ['fill-white', 'fill-none', 'fill-solid'].includes(props.iconName) &&
    props.iconColor === 'dark-text-100' &&
    !props.isSelected
  ) {
    return 'ring-2 ring-grey-600 rounded-full'
  }

  if (
    props.colorScheme === 'light' &&
    ['fill-white', 'fill-none', 'fill-solid'].includes(props.iconName) &&
    props.iconColor === 'light-text-100' &&
    !props.isSelected
  ) {
    return 'ring-2 ring-grey-200 rounded-full'
  }
  return ''
})

const ozIconSize = computed(() => {
  if (isShapeIcon.value && props.mode === 'elementModifierSecondaryMenu') {
    return 20
  }
  if (device.touchable) {
    return 28
  }
  return 24
})

const whiteboardSvgIconSize = computed(() => {
  if (device.touchable && isElementModifierIcon.value) {
    return 28
  }
  if (device.touchable && !isElementModifierIcon.value) {
    return isColorIcon.value ? 36 : 30
  }
  return 24
})

const ozBaseButtonSizeClass = computed(() => {
  if (!hasIcon.value) {
    return 'h-auto w-auto'
  }
  if (device.touchable && !isElementModifierIcon.value) {
    return 'h-9 w-9'
  }
  if (props.mode === 'elementModifierPrimaryMenu') {
    return 'h-8 w-8'
  }
  if (['pen', 'highlighter', 'eraser'].includes(props.iconName)) {
    return 'h-8 w-8'
  }
  return 'h-7 w-7'
})

const ozBaseButtonTestId = computed(() => {
  if (props.label) {
    return `WhiteboardToolButton-${sanitizeAndCamelCaseTitle(props.label)}`
  } else if (props.iconColor) {
    return `WhiteboardToolButton-${sanitizeAndCamelCaseTitle(props.iconColor)}`
  } else if (props.iconName) {
    return `WhiteboardToolButton-${sanitizeAndCamelCaseTitle(props.iconName)}`
  } else if (props.htmlLabel) {
    return `WhiteboardToolButton-${sanitizeAndCamelCaseTitle(props.htmlLabel)}`
  } else {
    return 'WhiteboardToolButton'
  }
})
</script>

<template>
  <div class="relative flex justify-center items-center shrink-0">
    <!-- Chevron on the left-->

    <!-- Button -->
    <OzBaseButton
      ref="buttonRef"
      :data-testid="ozBaseButtonTestId"
      :size-preset="undefined"
      :color-scheme="undefined"
      :dark-mode="colorScheme === 'dark'"
      :label="label"
      :class="[
        // Shape
        ozBaseButtonSizeClass,
        'rounded-full',
        hasIcon ? 'p-0' : 'px-2',
        'm-0',

        // Color
        {
          'hhover:bg-transparent hactive:bg-transparent focus-visible:bg-transparent':
            isColorIcon && !isElementModifierIcon,
        },

        bgColorClass != null
          ? bgColorClass
          : {
              'bg-transparent': true,
              'hhover:bg-light-ui-100/20 hactive:bg-light-ui-100/40 ': colorScheme === 'dark' && !isSelected,
              'hhover:bg-dark-ui-100/10 hactive:bg-dark-ui-100/20': colorScheme === 'light' && !isSelected,
            },

        // Focus
        !isSelected && colorScheme === 'dark'
          ? ['focus-visible:ring-3', 'focus-visible:ring-canary-500', 'focus-visible:bg-modal-overlay-canary/20']
          : '',
        !isSelected && colorScheme === 'light'
          ? ['focus-visible:ring-3', 'focus-visible:ring-grape-500', 'focus-visible:bg-modal-overlay-grape/20']
          : '',
        isSelected && colorScheme === 'dark' ? ['focus-visible:ring-3', 'focus-visible:ring-canary-500'] : '',
        isSelected && colorScheme === 'light' ? ['focus-visible:ring-3', 'focus-visible:ring-grape-500'] : '',

        isAppUsing('sandboxTextFormatting') &&
        (isColorIcon || isFillTypeIcon) &&
        isElementModifierSecondaryIcon &&
        isSelected
          ? ['ring-[1.5px]', colorScheme === 'dark' ? 'ring-canary-500' : 'ring-grape-500']
          : '',

        !isAppUsing('sandboxTextFormatting') && isElementModifierSecondaryIcon && isSelected
          ? ['ring-[1.5px]', colorScheme === 'dark' ? 'ring-canary-500' : 'ring-grape-500']
          : '',

        // Transition
        'transition-all',
        'duration-250',
        'ease-in-out',

        // Children positioning
        'flex',
        'items-center',
        'justify-center',
        'relative',

        // Children styling
        'group',
        'text-body-small',
        isAppUsing('sandboxTextFormatting') ? 'font-medium' : 'font-semibold', // goodcheck-disable-line
        iconColorClass != null
          ? iconColorClass
          : {
              'text-light-text-200': colorScheme === 'dark' && !isSelected,
              'text-dark-text-200': colorScheme === 'light' && !isSelected,
              'text-light-text-100': isSelected && colorScheme === 'light',
              'text-dark-text-100': isSelected && colorScheme === 'dark',
            },
      ]"
      @pointerenter="onPointerEnter"
      @focus="onPointerEnter"
      @pointerleave="onPointerLeave"
      @blur="onPointerLeave"
      @pointerup="onPointerUp"
      @click="onPointerDown"
    >
      <template v-if="hasIcon">
        <OzIcon v-if="!isSvgIcon" :font="iconFont" :name="iconName" :size="ozIconSize" />
        <WhiteboardSvgIcon
          v-else
          :name="iconName"
          :size="whiteboardSvgIconSize"
          :color-scheme="colorScheme"
          :class="[iconColorClass, extraClass]"
          :is-hovered="isHovered"
          :is-active="isActive"
          :is-selected="isSelected"
          :is-opaque-outline-color="
            (colorScheme === 'dark' && iconColor === 'dark-text-100') ||
            (colorScheme === 'light' && iconColor === 'light-text-100')
          "
          :fill-text-color-class="iconColor && getTailwindColorClass(SHAPE_BORDER_TO_FAKE_FILL_COLOR_MAP[iconColor])"
          :mode="mode"
        />
      </template>
      <template v-else>
        <span
          v-if="htmlLabel"
          :class="[
            'text-body-small',
            {
              'leading-[30px]': mode === 'elementModifierPrimaryMenu',
              'leading-[26px]': mode === 'elementModifierSecondaryMenu',
            },
          ]"
          v-html="htmlLabel"
        />
        <span
          v-else
          :class="[
            'text-body-small',
            {
              'leading-[30px]': mode === 'elementModifierPrimaryMenu',
              'leading-[26px]': mode === 'elementModifierSecondaryMenu',
            },
          ]"
        >
          {{ label }}
        </span>
      </template>
    </OzBaseButton>
  </div>
</template>
